/**
 * DOM and SVG helpers for the /help/contact page.
 *
 * WHAT THIS FILE DOES:
 * - Provides tiny DOM helper utilities for the help-contact UI.
 * - Centralizes SVG icon creation to keep UI rendering clean.
 * - Exports the Gist wordmark used across help screens.
 *
 * WHY THIS EXISTS:
 * - The help-contact UI is built with vanilla DOM APIs (no framework).
 * - Reusing element + icon helpers avoids duplication and keeps render code short.
 * - The wordmark and iconography must stay consistent across login + form views.
 *
 * ARCHITECTURE CONTEXT:
 * - Used by packages/backend/src/client/help-contact.ts (login + error views).
 * - Used by packages/backend/src/client/help-contact/support-form-controller.ts (support form).
 * - Mirrors the extension support modal look/feel without copying whole markup.
 *
 * DESIGN NOTES:
 * - SVG paths match the extension help modal icons for visual parity.
 * - Icons are lightweight and inline for easy theming via CSS.
 *
 * RELATED FILES:
 * - packages/backend/public/static/help-contact.css (styles for these elements)
 * - packages/backend/src/ssr/help-contact.ts (mounts the help-contact bundle)
 * - packages/chrome-plugin/extension/ui/options/support-message-modal.js (reference)
 *
 * NON-GOALS:
 * - No DOM diffing or rendering abstraction.
 * - No state management; pure element factories only.
 *
 * FILE HEADER SIZE TARGET:
 * - Keep this header between 50-120 lines for LLM readability.
 */

/**
 * Create an element with attributes and children.
 *
 * @param tag - HTML tag name.
 * @param attrs - Attribute map (boolean values set empty attributes).
 * @param children - Child nodes or strings.
 * @returns Newly created HTMLElement.
 */
export function createElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string | boolean | number>,
  ...children: (string | Node)[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      if (typeof value === 'boolean') {
        if (value) el.setAttribute(key, '');
      } else {
        el.setAttribute(key, String(value));
      }
    }
  }
  for (const child of children) {
    if (typeof child === 'string') {
      el.appendChild(document.createTextNode(child));
    } else {
      el.appendChild(child);
    }
  }
  return el;
}

/**
 * Create an SVG icon from path data.
 *
 * @param viewBox - SVG viewBox string.
 * @param paths - SVG path d strings.
 * @param attrs - Optional attribute map for the SVG element.
 * @returns SVG element with the supplied paths.
 */
export function createSvgIcon(viewBox: string, paths: string[], attrs?: Record<string, string>): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', viewBox);
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      svg.setAttribute(key, value);
    }
  }
  for (const d of paths) {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', d);
    svg.appendChild(path);
  }
  return svg;
}

/**
 * Create the Gist wordmark logo used in the help header.
 *
 * @returns Anchor element containing the logo.
 */
export function createWordmark(): HTMLElement {
  const link = createElement('a', { href: 'https://gist.is', class: 'help-page__wordmark' });

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '62 25 76 150');

  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
  const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
  gradient.setAttribute('id', 'bolt');
  gradient.setAttribute('x1', '0%');
  gradient.setAttribute('y1', '0%');
  gradient.setAttribute('x2', '100%');
  gradient.setAttribute('y2', '100%');

  const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
  stop1.setAttribute('offset', '0%');
  stop1.setAttribute('stop-color', '#FF6B6B');

  const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
  stop2.setAttribute('offset', '50%');
  stop2.setAttribute('stop-color', '#FFD93D');

  const stop3 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
  stop3.setAttribute('offset', '100%');
  stop3.setAttribute('stop-color', '#FF8E53');

  gradient.appendChild(stop1);
  gradient.appendChild(stop2);
  gradient.appendChild(stop3);
  defs.appendChild(gradient);
  svg.appendChild(defs);

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M110 35L70 100h25l-5 65 40-75h-25z');
  path.setAttribute('fill', 'url(#bolt)');
  svg.appendChild(path);

  link.appendChild(svg);
  link.appendChild(createElement('span', {}, 'Gist'));

  return link;
}

/**
 * Create the heart icon used in help headers.
 */
export function createHeartIcon(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z');
  svg.appendChild(path);

  return svg;
}

/**
 * Create the microphone icon.
 */
export function createMicIcon(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('aria-hidden', 'true');

  const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path1.setAttribute('d', 'M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z');

  const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path2.setAttribute('d', 'M19 10v2a7 7 0 0 1-14 0v-2');

  svg.appendChild(path1);
  svg.appendChild(path2);

  return svg;
}

/**
 * Create a filled play icon.
 */
export function createPlayIcon(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'currentColor');
  svg.setAttribute('aria-hidden', 'true');

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M8 5v14l11-7z');
  svg.appendChild(path);

  return svg;
}

/**
 * Create a filled pause icon.
 */
export function createPauseIcon(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'currentColor');
  svg.setAttribute('aria-hidden', 'true');

  const rect1 = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  rect1.setAttribute('x', '6');
  rect1.setAttribute('y', '5');
  rect1.setAttribute('width', '4');
  rect1.setAttribute('height', '14');

  const rect2 = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  rect2.setAttribute('x', '14');
  rect2.setAttribute('y', '5');
  rect2.setAttribute('width', '4');
  rect2.setAttribute('height', '14');

  svg.appendChild(rect1);
  svg.appendChild(rect2);

  return svg;
}

/**
 * Create a stop icon for the recording button.
 */
export function createStopIcon(): SVGSVGElement {
  return createSvgIcon('0 0 24 24', ['M6 6h12v12H6z']);
}

/**
 * Create a close (X) icon.
 */
export function createCloseIcon(): SVGSVGElement {
  return createSvgIcon('0 0 24 24', ['M18 6L6 18', 'M6 6l12 12']);
}

/**
 * Create a refresh icon for rerecord action.
 */
export function createRefreshIcon(): SVGSVGElement {
  return createSvgIcon('0 0 24 24', ['M21 12a9 9 0 1 1-3-6.7', 'M22 4v6h-6']);
}
